import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  Globe,
  Lock,
  ArrowUpRight,
  BookOpen,
  Link2,
  Sparkles,
  RefreshCcw,
  Tag,
  ImagePlus,
  AlertTriangle,
  CheckCircle2,
  LayoutList,
} from "lucide-react";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * Plain-English, example-driven reference for the Public Catalog Page
 * feature — what it is, what shows per plan, exactly what does and
 * doesn't trigger a rebuild of the cached page, and how the "New"
 * badge behaves. Companion to CatalogGuide.jsx, same design system.
 */

const ACCENTS = {
  lilac: { chip: "bg-lilac", chipText: "text-ink", soft: "bg-lilac/25", ring: "ring-lilac" },
  mint: { chip: "bg-mint", chipText: "text-ink", soft: "bg-mint/25", ring: "ring-mint" },
  peach: { chip: "bg-peach", chipText: "text-ink", soft: "bg-peach/25", ring: "ring-peach" },
  hotpink: { chip: "bg-hotpink", chipText: "text-white", soft: "bg-hotpink/10", ring: "ring-hotpink" },
  ink: { chip: "bg-ink", chipText: "text-cream", soft: "bg-ink/[0.06]", ring: "ring-ink" },
};

function FieldCard({ icon: Icon, label, desc, example, note, accent }) {
  const a = ACCENTS[accent];
  return (
    <div className="rounded-2xl border border-ink/10 bg-white p-4">
      <div className="flex items-center gap-2">
        <span className={`grid h-7 w-7 flex-none place-items-center rounded-lg ${a.chip} ${a.chipText}`}>
          <Icon size={13} strokeWidth={2.4} />
        </span>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-ink">{label}</p>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-ink/70">{desc}</p>
      {example && (
        <div className={`mt-2.5 rounded-xl ${a.soft} px-3 py-2`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-ink/50">Example</p>
          <p className="mt-0.5 text-[12px] leading-relaxed text-ink/80">{example}</p>
        </div>
      )}
      {note && <p className="mt-2 text-[11px] leading-relaxed text-ink/45">{note}</p>}
    </div>
  );
}

function GuideSection({ id, icon: Icon, accent, name, tagline, what, useFor, checkWhen, fields, planNote, children }) {
  const a = ACCENTS[accent];
  return (
    <section id={id} className="scroll-mt-24 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <span className={`grid h-12 w-12 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText}`}>
            <Icon size={20} strokeWidth={2.2} />
          </span>
          <div>
            <p className={`text-xs font-bold uppercase tracking-[0.2em] ${accent === "hotpink" ? "text-hotpink" : "text-ink/50"}`}>
              {tagline}
            </p>
            <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">{name}</h2>
          </div>
        </div>
        {planNote && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-ink/[0.03] px-3 py-1.5 text-[11px] font-semibold text-ink/60">
            <Lock size={11} /> {planNote}
          </span>
        )}
      </div>

      {what && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">{what}</p>}

      {(useFor || checkWhen) && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {useFor && (
            <div className="rounded-2xl bg-ink/[0.03] p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">Use it for</p>
              <p className="mt-1 text-[13px] leading-relaxed text-ink/70">{useFor}</p>
            </div>
          )}
          {checkWhen && (
            <div className="rounded-2xl bg-ink/[0.03] p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">Check it when</p>
              <p className="mt-1 text-[13px] leading-relaxed text-ink/70">{checkWhen}</p>
            </div>
          )}
        </div>
      )}

      {children}

      {fields && fields.length > 0 && (
        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">What you'll see, field by field</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {fields.map((f) => (
              <FieldCard key={f.label} accent={accent} {...f} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default function PublicCatalogGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Public Catalog Page Guide",
    description: "What the Public Catalog Page is, what shows per plan, and exactly what triggers the page to refresh with your latest changes.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/public-catalog-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Public Catalog Page Guide", item: "https://checkoutdm.com/public-catalog-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Public Catalog Page Guide | Checkout DM"
        description="What the Public Catalog Page is, what shows per plan, and exactly what triggers the page to refresh with your latest changes."
        path="/public-catalog-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          The Public Catalog Page, explained
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          A shareable, read-only version of your catalog at a link you can post anywhere —
          your Instagram bio, WhatsApp status, or a Linktree. Customers can browse your
          products and add them to a cart, but every order still finishes inside your
          Instagram DMs exactly like it does today. This page reads from a snapshot of your
          catalog, not your live data — this guide explains exactly when that snapshot updates.
        </p>
        <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          <Link to="/catalog-guide" className="inline-flex items-center gap-1.5 font-semibold text-hotpink hover:underline">
            <BookOpen size={13} /> What the Catalog screen means →
          </Link>
        </p>

        {/* Quick nav */}
        <div className="mt-6 flex flex-wrap gap-2">
          {[
            { id: "overview", label: "Overview", icon: Globe, accent: "lilac" },
            { id: "getting-there", label: "Your link", icon: Link2, accent: "mint" },
            { id: "what-shows", label: "What shows, by plan", icon: LayoutList, accent: "peach" },
            { id: "refresh", label: "When the page updates", icon: RefreshCcw, accent: "lilac" },
            { id: "new-badge", label: "The \"New\" badge", icon: Sparkles, accent: "mint" },
            { id: "coupons", label: "Coupons on this page", icon: Tag, accent: "peach" },
            { id: "troubleshooting", label: "Troubleshooting", icon: AlertTriangle, accent: "hotpink" },
          ].map((t) => {
            const a = ACCENTS[t.accent];
            return (
              <a
                key={t.id}
                href={`#${t.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full ${a.chip} ${a.chipText} px-3.5 py-1.5 text-xs font-bold`}
              >
                <t.icon size={12} /> {t.label}
              </a>
            );
          })}
        </div>

        <div className="mt-8 space-y-6">
          {/* Overview */}
          <GuideSection
            id="overview"
            icon={Globe}
            accent="lilac"
            name="What this page is"
            tagline="A snapshot, not a live view"
            planNote="Creator and above"
            what="Turning on the Public Catalog Page (from the toggle on your Catalog screen) publishes a read-only storefront at your own link. It is not a live query against your database on every visit — a background job pre-builds a single JSON snapshot of your store (products, pricing, and — depending on your plan — branding, social links, and active discounts) and saves it to a cache table. Every visitor sees that saved snapshot, which is what makes the page fast even under heavy traffic, but it also means changes you make in your dashboard don't appear until something rebuilds that snapshot."
            useFor="Sharing one link that shows your whole catalog to people who aren't ready to DM you yet — your Instagram bio, a WhatsApp broadcast, a Linktree page."
            checkWhen="A customer says they can't see a product, image, price, or discount you know you've already set up — the cause is almost always that the snapshot hasn't rebuilt since your change, not that the change didn't save."
          />

          {/* Your link */}
          <GuideSection
            id="getting-there"
            icon={Link2}
            accent="mint"
            name="Your link"
            tagline="One per business"
            fields={[
              {
                icon: Link2,
                label: "URL format",
                desc: "checkoutdm.com/store/your-instagram-username — the same Instagram username connected to your account, so there's nothing extra to set up.",
                example: "checkoutdm.com/store/thehappycandleco",
              },
              {
                icon: CheckCircle2,
                label: "Turning it on",
                desc: "Toggle it on from the card at the top of your Catalog screen. Turning it on for the first time immediately builds the snapshot, so your page is live with current data right away.",
              },
              {
                icon: Lock,
                label: "Free plan",
                desc: "Not available on Free — the toggle isn't shown, and the link itself returns nothing for a Free-plan business even if someone guesses the URL.",
              },
            ]}
          />

          {/* What shows by plan */}
          <GuideSection
            id="what-shows"
            icon={LayoutList}
            accent="peach"
            name="What shows, by plan"
            tagline="More unlocks as you go up a tier"
            what="The snapshot only ever includes what your current plan is entitled to — this is decided at the moment the snapshot is built, from your plan at that time."
            fields={[
              {
                icon: LayoutList,
                label: "Creator",
                desc: "Your digital products (name, price, description, main photo, category) with extended descriptions and FAQs on a basic storefront — no business address footer, extra photos, or coupons.",
              },
              {
                icon: LayoutList,
                label: "Creator",
                desc: "Your digital products (name, price, description, main photo, category) with extended descriptions and FAQs on a basic storefront — no business address footer, extra photos, or coupons.",
              },
              {
                icon: LayoutList,
                label: "Starter",
                desc: "Everything in Creator, plus physical products, 1 extra photo per product (2 total with the main image), and your business address in the footer.",
              },
              {
                icon: LayoutList,
                label: "Growth",
                desc: "Everything in Starter, plus a 2nd extra photo per product (3 total), your social media links, and any active coupons/discounts.",
              },
              {
                icon: ImagePlus,
                label: "Pro",
                desc: "Everything in Growth, plus a 3rd extra photo per product (4 total with the main image), your logo, footer tagline, website link, and phone number in the footer.",
              },
            ]}
          />

          {/* Refresh triggers */}
          <GuideSection
            id="refresh"
            icon={RefreshCcw}
            accent="lilac"
            name="When the page actually updates"
            tagline="Only specific actions rebuild the snapshot"
            what="Only a handful of actions rebuild the cached snapshot behind this page. If your change isn't in this list, the public page will keep showing the old data until something else on this list happens — there's no automatic timer that refreshes it on its own."
            fields={[
              {
                icon: CheckCircle2,
                label: "Adding a new product",
                desc: "Always rebuilds — whether added one at a time or via Pro's bulk photo upload.",
              },
              {
                icon: CheckCircle2,
                label: "Deleting a product",
                desc: "Always rebuilds.",
              },
              {
                icon: AlertTriangle,
                label: "Editing a product",
                desc: "Only rebuilds if the price or the main photo changed. Editing just the name, description, or a Pro merchant's additional photos does NOT rebuild the snapshot on its own.",
                note: "This is the most common cause of \"I added photos but they're not showing\" — see Troubleshooting below.",
              },
              {
                icon: CheckCircle2,
                label: "Turning the toggle on",
                desc: "Rebuilds once, at the moment you switch it from off to on.",
              },
              {
                icon: AlertTriangle,
                label: "Turning the toggle off, or other settings",
                desc: "Does not rebuild anything — there's nothing to rebuild since the page stops serving once it's off.",
              },
            ]}
          />

          {/* New badge */}
          <GuideSection
            id="new-badge"
            icon={Sparkles}
            accent="mint"
            name={'The "New" badge'}
            tagline="Calculated live, not baked into the snapshot"
            what="A product shows a 'New' badge for exactly 14 days from the date it was added to your catalog. Unlike everything else on this page, the badge isn't decided when the snapshot is built — it's calculated fresh in the visitor's browser every time the page loads, by comparing the product's original add-date (which IS stored in the snapshot) against the current date."
            fields={[
              {
                icon: Sparkles,
                label: "What counts as \"new\"",
                desc: "Less than 14 days since the product was first added to your catalog — not since it was last edited.",
              },
              {
                icon: CheckCircle2,
                label: "It expires on its own",
                desc: "Because the 14-day check runs live, the badge disappears exactly on schedule even if the snapshot hasn't rebuilt in a month. You don't need to trigger a rebuild to make an old badge go away.",
              },
              {
                icon: RefreshCcw,
                label: "The one thing that does need a rebuild",
                desc: "The product has to be in the snapshot in the first place. Since adding a product always triggers a rebuild (see above), a brand-new product's badge shows correctly from the start.",
              },
            ]}
          />

          {/* Coupons */}
          <GuideSection
            id="coupons"
            icon={Tag}
            accent="peach"
            name="Coupons on this page"
            tagline="Growth and Pro only"
            what="Active discounts are included in the snapshot the same way products are — as a saved copy, not a live check. Creating, editing, deleting, or toggling a coupon does not rebuild the snapshot by itself."
            fields={[
              {
                icon: AlertTriangle,
                label: "A brand-new coupon",
                desc: "Won't appear on the public page until the next rebuild happens for some other reason — e.g. you also edit a product's price or photo, or you use the manual rebuild option.",
              },
              {
                icon: AlertTriangle,
                label: "A coupon that just expired",
                desc: "The expiry date is only checked at the moment the snapshot is built. If a time-based coupon expires and nothing else rebuilds the page afterward, it can keep showing as active on the public page past its real expiry — even though it will correctly be rejected if a customer tries to actually use it at checkout in DMs.",
              },
            ]}
          />

          {/* Troubleshooting */}
          <GuideSection
            id="troubleshooting"
            icon={AlertTriangle}
            accent="hotpink"
            name="Troubleshooting"
            tagline={'Common "it\'s not showing up" cases'}
            fields={[
              {
                icon: ImagePlus,
                label: "I added extra photos to a product but can't see them",
                desc: "Editing a product's additional photos alone doesn't rebuild the snapshot. Make a small price change and change it back (or re-save the main photo), or ask us to trigger a manual rebuild for your store.",
              },
              {
                icon: Tag,
                label: "My new coupon isn't showing",
                desc: "Coupon changes don't rebuild the snapshot on their own. It'll appear the next time something else triggers a rebuild.",
              },
              {
                icon: AlertTriangle,
                label: "An expired coupon is still showing",
                desc: "Expected until the next rebuild — the expiry check only runs when the snapshot is built, not on every page view. It won't actually apply for the customer at checkout.",
              },
              {
                icon: Lock,
                label: "The page shows nothing / 404s",
                desc: "Either the toggle is off, or your plan doesn't include this feature (Free plan). Check the toggle on your Catalog screen first.",
              },
            ]}
          />
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Still stuck?</p>
            <p className="mt-1 max-w-md text-sm text-ink/70">
              If something about the Public Catalog Page still doesn't make sense after reading this, reach out and we'll add it here for the next person too.
            </p>
          </div>
          <a
            href="/plans"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
          >
            See plans <ArrowUpRight size={14} />
          </a>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
